import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetchJson } from '../../apiClient';
import './TimerTBR.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

function TimerTBR({ factFictionMode }) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('timer');

  return (
    <div className="timer-tbr-container">
      <div className="timer-tbr-tabs">
        <button
          className={`timer-tbr-tab ${activeSection === 'timer' ? 'active' : ''}`}
          onClick={() => setActiveSection('timer')}
        >
          ⏱️ {t('bookClub.timer.readingTimer')}
        </button>
        <button
          className={`timer-tbr-tab ${activeSection === 'tbr' ? 'active' : ''}`}
          onClick={() => setActiveSection('tbr')}
        >
          📚 {t('bookClub.timer.tbrQueue')}
        </button>
      </div>

      {activeSection === 'timer' && <ReadingTimer instance={instance} />}
      {activeSection === 'tbr' && <TBRQueue instance={instance} />}
    </div>
  );
}

/* ==================== Reading Timer ==================== */
function ReadingTimer({ instance }) {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [bookTitle, setBookTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sessionData, statsData] = await Promise.all([
        apiFetchJson(instance, '/api/readingtimer/my'),
        apiFetchJson(instance, '/api/readingtimer/stats').catch(() => null),
      ]);
      setSessions(unwrap(sessionData));
      setStats(statsData);
    } catch (err) {
      setError(err.message || 'Failed to load timer data');
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const handleStart = () => {
    setRunning(true);
    setElapsed(0);
    startTimeRef.current = Date.now();
    intervalRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
  };

  const handleStop = async () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    setRunning(false);
    const durationSeconds = elapsed;
    if (durationSeconds < 5) return; // too short
    setSaving(true);
    try {
      await apiFetchJson(instance, '/api/readingtimer', {
        method: 'POST',
        body: JSON.stringify({
          durationSeconds,
          bookTitle: bookTitle.trim() || undefined,
        }),
      });
      setElapsed(0);
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to save session');
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  if (loading) return <div className="timer-loading">{t('bookClub.timer.loadingTimer')}</div>;
  if (error) return <div className="timer-error">{error} <button onClick={fetchData}>Retry</button></div>;

  return (
    <div className="timer-section">
      {/* Timer display */}
      <div className="timer-display-card">
        <div className="timer-circle">
          <span className="timer-time">{formatTime(elapsed)}</span>
        </div>
        <input
          type="text"
          className="timer-book-input"
          placeholder={t('bookClub.timer.whatReading')}
          value={bookTitle}
          onChange={e => setBookTitle(e.target.value)}
          disabled={running}
        />
        <div className="timer-controls">
          {!running ? (
            <button className="timer-start-btn" onClick={handleStart}>
              ▶ {t('bookClub.timer.start')}
            </button>
          ) : (
            <button className="timer-stop-btn" onClick={handleStop} disabled={saving}>
              {saving ? t('bookClub.timer.saving') : '⏹ ' + t('bookClub.timer.stop')}
            </button>
          )}
        </div>
      </div>

      {/* Stats card */}
      {stats && (
        <div className="timer-stats-card">
          <div className="timer-stat">
            <span className="timer-stat-value">{stats.totalSessions ?? 0}</span>
            <span className="timer-stat-label">{t('bookClub.timer.sessions')}</span>
          </div>
          <div className="timer-stat">
            <span className="timer-stat-value">{stats.totalMinutes ?? Math.round((stats.totalSeconds ?? 0) / 60)}</span>
            <span className="timer-stat-label">{t('bookClub.timer.minutes')}</span>
          </div>
          <div className="timer-stat">
            <span className="timer-stat-value">{stats.averageMinutes ?? '—'}</span>
            <span className="timer-stat-label">{t('bookClub.timer.avgSession')}</span>
          </div>
        </div>
      )}

      {/* Session history */}
      {sessions.length > 0 && (
        <div className="timer-history">
          <h4 className="timer-history-title">{t('bookClub.timer.recentSessions')}</h4>
          {sessions.slice(0, 10).map(s => (
            <div key={s.id} className="timer-history-row">
              <span className="timer-history-book">{s.bookTitle || t('bookClub.timer.untitled')}</span>
              <span className="timer-history-duration">
                {s.durationMinutes ?? Math.round((s.durationSeconds ?? 0) / 60)} min
              </span>
              <span className="timer-history-date">
                {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ==================== TBR Queue ==================== */
function TBRQueue({ instance }) {
  const { t } = useTranslation();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newBook, setNewBook] = useState('');
  const [adding, setAdding] = useState(false);
  const [shuffling, setShuffling] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetchJson(instance, '/api/tbrqueue');
      setQueue(unwrap(data));
    } catch (err) {
      setError(err.message || 'Failed to load TBR queue');
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newBook.trim()) return;
    setAdding(true);
    try {
      await apiFetchJson(instance, '/api/tbrqueue', {
        method: 'POST',
        body: JSON.stringify({ bookTitle: newBook.trim() }),
      });
      setNewBook('');
      fetchQueue();
    } catch (err) {
      setError(err.message || 'Failed to add book');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (id) => {
    setDeletingId(id);
    try {
      await apiFetchJson(instance, `/api/tbrqueue/${id}`, { method: 'DELETE' });
      setQueue(prev => prev.filter(b => b.id !== id));
    } catch (err) {
      setError(err.message || 'Failed to remove book');
    } finally {
      setDeletingId(null);
    }
  };

  const handleMove = async (index, direction) => {
    const newQueue = [...queue];
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= newQueue.length) return;
    [newQueue[index], newQueue[swapIdx]] = [newQueue[swapIdx], newQueue[index]];
    setQueue(newQueue);
    try {
      await apiFetchJson(instance, '/api/tbrqueue/reorder', {
        method: 'PUT',
        body: JSON.stringify({ orderedIds: newQueue.map(b => b.id) }),
      });
    } catch (err) {
      setError(err.message || 'Failed to reorder');
      fetchQueue();
    }
  };

  const handleShuffle = async () => {
    setShuffling(true);
    try {
      const data = await apiFetchJson(instance, '/api/tbrqueue/shuffle');
      setQueue(unwrap(data));
    } catch (err) {
      setError(err.message || 'Failed to shuffle');
    } finally {
      setTimeout(() => setShuffling(false), 400);
    }
  };

  const monthsWaiting = (dateStr) => {
    if (!dateStr) return null;
    const added = new Date(dateStr);
    const now = new Date();
    const months = (now.getFullYear() - added.getFullYear()) * 12 + (now.getMonth() - added.getMonth());
    return months > 0 ? months : null;
  };

  if (loading) return <div className="tbr-loading">{t('bookClub.timer.loadingTbr')}</div>;
  if (error) return <div className="tbr-error">{error} <button onClick={fetchQueue}>Retry</button></div>;

  return (
    <div className="tbr-section">
      {/* Add book form */}
      <form className="tbr-add-form" onSubmit={handleAdd}>
        <input
          type="text"
          className="tbr-input"
          placeholder={t('bookClub.timer.addBookPlaceholder')}
          value={newBook}
          onChange={e => setNewBook(e.target.value)}
          required
        />
        <button type="submit" className="tbr-add-btn" disabled={adding}>
          {adding ? '…' : t('bookClub.timer.add')}
        </button>
        <button
          type="button"
          className={`tbr-shuffle-btn ${shuffling ? 'shuffling' : ''}`}
          onClick={handleShuffle}
          disabled={shuffling || queue.length < 2}
          title="Shuffle queue"
        >
          🔀
        </button>
      </form>

      {/* Queue list */}
      <div className="tbr-queue-list">
        {queue.length === 0 ? (
          <p className="tbr-empty">{t('bookClub.timer.tbrEmpty')}</p>
        ) : (
          queue.map((book, idx) => {
            const waiting = monthsWaiting(book.addedAt || book.createdAt);
            return (
              <div key={book.id} className="tbr-queue-item">
                <span className="tbr-rank">{idx + 1}</span>
                <div className="tbr-item-info">
                  <span className="tbr-book-title">{book.bookTitle || book.title || 'Untitled'}</span>
                  {waiting && (
                    <span className="tbr-waiting">{t('bookClub.timer.waitingMonths', { count: waiting })}</span>
                  )}
                </div>
                <div className="tbr-item-actions">
                  <button
                    className="tbr-move-btn"
                    onClick={() => handleMove(idx, 'up')}
                    disabled={idx === 0}
                    title="Move up"
                  >
                    ▲
                  </button>
                  <button
                    className="tbr-move-btn"
                    onClick={() => handleMove(idx, 'down')}
                    disabled={idx === queue.length - 1}
                    title="Move down"
                  >
                    ▼
                  </button>
                  <button
                    className="tbr-remove-btn"
                    onClick={() => handleRemove(book.id)}
                    disabled={deletingId === book.id}
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default TimerTBR;
